import React, { useEffect, useState } from 'react';
import mammoth from 'mammoth';
import { documentsAPI } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Download, Loader2, FileWarning } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Inline preview for compliance documents (iter71.3).
 *
 * The authenticated download proxy returns a blob — we create a
 * blob-URL and render it inline:
 *   - PDFs  → native <iframe> (every modern browser renders PDFs)
 *   - Images → native <img>
 *   - ·docx → mammoth converts XML to HTML in the browser
 *   - ·doc  → cannot render in-browser; fall back to a download CTA
 *
 * The file bytes never leave the user's browser — the request still
 * carries the user's bearer token via documentsAPI.download, so RBAC
 * on the backend is preserved.
 */
const kindOf = (doc) => {
  const ct = (doc?.file_type || doc?.content_type || '').toLowerCase();
  const name = (doc?.file_name || '').toLowerCase();
  if (ct.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  if (ct.startsWith('image/') || /\.(jpe?g|png)$/i.test(name)) return 'image';
  if (
    ct.includes('wordprocessingml') ||
    name.endsWith('.docx')
  ) return 'docx';
  if (ct === 'application/msword' || name.endsWith('.doc')) return 'doc';
  return 'other';
};

export default function DocumentPreviewDialog({ open, onOpenChange, doc }) {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [docxHtml, setDocxHtml] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const kind = doc ? kindOf(doc) : null;

  useEffect(() => {
    // Reset + fetch whenever a new doc opens
    if (!open || !doc) {
      // Revoke any previous blob to avoid leaks
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
      setDocxHtml(null);
      setErrorMsg(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let createdUrl = null;

    const load = async () => {
      setLoading(true);
      setErrorMsg(null);
      setDocxHtml(null);
      try {
        const resp = await documentsAPI.download(doc.id);
        if (cancelled) return;
        const blob = resp.data;

        if (kind === 'docx') {
          // Convert ·docx → HTML entirely in the browser.
          const buffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
          if (cancelled) return;
          setDocxHtml(result.value || '<p><em>Document is empty.</em></p>');
        } else if (kind === 'doc') {
          // Legacy binary ·doc — browser can't render; offer download.
          setErrorMsg(
            'Legacy ·doc files cannot be previewed inline. Please download to view, or ask the uploader to re-upload as ·docx or PDF.'
          );
        } else {
          createdUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(createdUrl);
            return;
          }
          setBlobUrl(createdUrl);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('preview failed', err);
          setErrorMsg('Failed to load preview. The file may be unavailable.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id]);

  const handleDownload = async () => {
    if (!doc) return;
    try {
      const response = await documentsAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || `document-${doc.id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download document');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#1A1A1A] border-white/10 text-[#F2F2F2] max-w-5xl w-[95vw] p-0 overflow-hidden"
        data-testid="document-preview-dialog"
      >
        <DialogHeader className="px-6 py-4 border-b border-white/10">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="text-base truncate">
                {doc?.file_name || 'Document preview'}
              </DialogTitle>
              {doc?.document_type_name && (
                <p className="text-xs text-[#A1A1AA] mt-1 truncate">
                  {doc.document_type_name}
                </p>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-white/10 text-[#F2F2F2] bg-[#2B2B2B] hover:bg-[#333]"
              onClick={handleDownload}
              data-testid="preview-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </DialogHeader>

        <div className="bg-[#0F0F0F] min-h-[60vh] max-h-[80vh] overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-20 text-[#A1A1AA]">
              <Loader2 className="w-6 h-6 mr-2 animate-spin" />
              Loading preview…
            </div>
          )}

          {!loading && errorMsg && (
            <div className="flex flex-col items-center justify-center text-center py-20 px-6">
              <FileWarning className="w-10 h-10 text-[#F59E0B] mb-3" />
              <p className="text-sm text-[#F2F2F2] max-w-md">{errorMsg}</p>
            </div>
          )}

          {!loading && !errorMsg && kind === 'pdf' && blobUrl && (
            <iframe
              src={blobUrl}
              title="PDF preview"
              className="w-full h-[75vh] bg-white"
              data-testid="preview-pdf-iframe"
            />
          )}

          {!loading && !errorMsg && kind === 'image' && blobUrl && (
            <div className="flex items-center justify-center p-4">
              <img
                src={blobUrl}
                alt={doc?.file_name || 'Document'}
                className="max-w-full max-h-[75vh] object-contain rounded"
                data-testid="preview-image"
              />
            </div>
          )}

          {!loading && !errorMsg && kind === 'docx' && docxHtml != null && (
            <div
              className="bg-white text-[#1A1A1A] mx-auto my-4 p-8 max-w-3xl rounded shadow-lg prose prose-sm"
              style={{ minHeight: '60vh' }}
              dangerouslySetInnerHTML={{ __html: docxHtml }}
              data-testid="preview-docx-html"
            />
          )}

          {!loading && !errorMsg && kind === 'other' && (
            <div className="flex flex-col items-center justify-center text-center py-20 px-6">
              <FileWarning className="w-10 h-10 text-[#F59E0B] mb-3" />
              <p className="text-sm text-[#F2F2F2] max-w-md">
                This file type cannot be previewed inline. Please download to view.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
